import type { Node, Edge } from '@xyflow/react'
import type { WorkflowNodeType, WorkflowNodeData } from '@/components/workflows/WorkflowCanvas'

/**
 * Serialized node format for storage
 */
export interface SerializedNode {
  id: string
  type: WorkflowNodeType
  position: { x: number; y: number }
  data: Record<string, unknown>
}

/**
 * Serialized edge format for storage
 */
export interface SerializedEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
}

/**
 * Workflow definition structure stored in database
 */
export interface WorkflowDefinition {
  version: number
  nodes: SerializedNode[]
  edges: SerializedEdge[]
  metadata?: Record<string, unknown>
}

/**
 * Current serialization version
 */
export const WORKFLOW_DEFINITION_VERSION = 1

/**
 * Convert ReactFlow nodes and edges to a serializable workflow definition
 */
export function serializeWorkflow(
  nodes: Node<WorkflowNodeData, WorkflowNodeType>[],
  edges: Edge[],
  metadata?: Record<string, unknown>
): WorkflowDefinition {
  const serializedNodes: SerializedNode[] = nodes.map((node) => ({
    id: node.id,
    type: node.type as WorkflowNodeType,
    position: {
      x: Math.round(node.position.x),
      y: Math.round(node.position.y),
    },
    data: cleanNodeData(node.data),
  }))

  const serializedEdges: SerializedEdge[] = edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
  }))

  return {
    version: WORKFLOW_DEFINITION_VERSION,
    nodes: serializedNodes,
    edges: serializedEdges,
    metadata,
  }
}

/**
 * Convert a stored workflow definition to ReactFlow nodes and edges
 */
export function deserializeWorkflow(definition: WorkflowDefinition): {
  nodes: Node<WorkflowNodeData, WorkflowNodeType>[]
  edges: Edge[]
} {
  // Handle potential version migrations in the future
  if (definition.version > WORKFLOW_DEFINITION_VERSION) {
    console.warn(
      `Workflow definition version ${definition.version} is newer than supported version ${WORKFLOW_DEFINITION_VERSION}`
    )
  }

  const nodes: Node<WorkflowNodeData, WorkflowNodeType>[] = definition.nodes.map((serialized) => ({
    id: serialized.id,
    type: serialized.type,
    position: serialized.position,
    data: serialized.data as WorkflowNodeData,
    selected: false,
    dragging: false,
  }))

  const edges: Edge[] = definition.edges.map((serialized) => ({
    id: serialized.id,
    source: serialized.source,
    target: serialized.target,
    sourceHandle: serialized.sourceHandle ?? undefined,
    targetHandle: serialized.targetHandle ?? undefined,
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#9333ea', strokeWidth: 2 },
  }))

  return { nodes, edges }
}

/**
 * Clean node data for serialization by removing transient properties
 */
function cleanNodeData(data: WorkflowNodeData): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data)) {
    // Skip transient runtime properties
    if (key === 'status' || key === 'errorMessage') {
      continue
    }

    // Skip functions
    if (typeof value === 'function') {
      continue
    }

    cleaned[key] = value
  }

  return cleaned
}

/**
 * Parse a JSON string into a workflow definition with validation
 */
export function parseWorkflowDefinition(json: string): WorkflowDefinition | null {
  try {
    const parsed = JSON.parse(json)

    // Validate basic structure
    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    // Ensure required fields exist
    if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) {
      return null
    }

    // Add version if missing (for backwards compatibility)
    if (typeof parsed.version !== 'number') {
      parsed.version = 1
    }

    return parsed as WorkflowDefinition
  } catch {
    return null
  }
}

/**
 * Convert workflow definition to JSON string
 */
export function stringifyWorkflowDefinition(definition: WorkflowDefinition): string {
  return JSON.stringify(definition, null, 2)
}

/**
 * Count the number of nodes by type in a workflow
 */
export function countNodesByType(definition: WorkflowDefinition): Record<WorkflowNodeType, number> {
  const counts: Record<WorkflowNodeType, number> = {
    trigger: 0,
    agent: 0,
    humanReview: 0,
    condition: 0,
    action: 0,
    end: 0,
  }

  for (const node of definition.nodes) {
    if (node.type in counts) {
      counts[node.type]++
    }
  }

  return counts
}

/**
 * Validate a workflow definition for completeness
 */
export function validateWorkflow(definition: WorkflowDefinition): {
  valid: boolean
  errors: string[]
  warnings: string[]
} {
  const errors: string[] = []
  const warnings: string[] = []

  // Check for at least one trigger
  const triggers = definition.nodes.filter((n) => n.type === 'trigger')
  if (triggers.length === 0) {
    errors.push('Workflow must have at least one trigger node')
  }

  // Check for orphan nodes (nodes with no connections)
  const connectedNodeIds = new Set<string>()
  for (const edge of definition.edges) {
    connectedNodeIds.add(edge.source)
    connectedNodeIds.add(edge.target)
  }

  for (const node of definition.nodes) {
    if (node.type !== 'trigger' && !connectedNodeIds.has(node.id)) {
      warnings.push(`Node "${node.data.label || node.id}" has no connections`)
    }
  }

  // Check for nodes with no outgoing connections (except end nodes)
  const nodesWithOutgoing = new Set(definition.edges.map((e) => e.source))
  for (const node of definition.nodes) {
    if (node.type !== 'end' && !nodesWithOutgoing.has(node.id)) {
      warnings.push(`Node "${node.data.label || node.id}" has no outgoing connections`)
    }
  }

  // Check for condition nodes with missing branches
  const conditionNodes = definition.nodes.filter((n) => n.type === 'condition')
  for (const condition of conditionNodes) {
    const outgoingEdges = definition.edges.filter((e) => e.source === condition.id)
    const hasTrue = outgoingEdges.some((e) => e.sourceHandle === 'true')
    const hasFalse = outgoingEdges.some((e) => e.sourceHandle === 'false')

    if (!hasTrue || !hasFalse) {
      warnings.push(`Condition node "${condition.data.label || condition.id}" is missing branch connections`)
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Create an empty workflow definition
 */
export function createEmptyWorkflow(): WorkflowDefinition {
  return {
    version: WORKFLOW_DEFINITION_VERSION,
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 250, y: 50 },
        data: {
          label: 'Start',
          triggerType: 'manual',
        },
      },
    ],
    edges: [],
  }
}

/**
 * Get a list of variables available at a given node position
 * Variables are outputs from nodes that come before the given node in the flow
 */
export function getAvailableVariables(
  definition: WorkflowDefinition,
  nodeId: string
): string[] {
  const variables: string[] = ['trigger_data', 'form_data']

  // Find all nodes that can reach the given node
  const predecessors = findPredecessors(definition, nodeId)

  for (const predId of predecessors) {
    const predNode = definition.nodes.find((n) => n.id === predId)
    if (!predNode) continue

    // Agent nodes have an output variable
    if (predNode.type === 'agent' && predNode.data.outputVariable) {
      variables.push(predNode.data.outputVariable as string)
    }

    // Human review nodes produce a decision variable
    if (predNode.type === 'humanReview') {
      variables.push(`${predNode.data.label || 'review'}_decision`)
    }
  }

  return variables
}

/**
 * Find all node IDs that can reach the given node
 */
function findPredecessors(definition: WorkflowDefinition, nodeId: string): Set<string> {
  const predecessors = new Set<string>()
  const visited = new Set<string>()

  function visit(id: string) {
    if (visited.has(id)) return
    visited.add(id)

    // Find all edges that target this node
    const incomingEdges = definition.edges.filter((e) => e.target === id)

    for (const edge of incomingEdges) {
      predecessors.add(edge.source)
      visit(edge.source)
    }
  }

  visit(nodeId)
  return predecessors
}
